import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { fieldMeetCertainCriteriaStyles } from '../styles';
import { useAutomationRuleConditions } from '../../use-conditions';
import { ConditionItemType, FieldMeetCertainCriteriaType } from '../../types';
import { useAzureProvider } from 'src/azure/context';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';
import { useSelectInput } from './use-select-input';

interface Props {
  id?: string;
  conditionId: string;
  validationError: string | null;
	isMultiSelect?: boolean;
}

export default function IterationAndAreaPathInput(props: Props) {
	const classes = fieldMeetCertainCriteriaStyles();
	const {applicationContext} = useAzureProvider();

	const selectInputHook = useSelectInput();
	const {conditions, setConditions, resetValidationErrors} = useAutomationRuleConditions();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: FieldMeetCertainCriteriaType = useMemo(() => {
		const foundConditions = conditions.find(item => item.id === props.conditionId) as ConditionItemType;
		return foundConditions!.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType;
	}, [conditions, props.conditionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		if (value === '') {
			getOptionsAsync();
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options]);

	const setOptionSelect = useCallback((value: string | boolean) => {
		const isNotEmpty = () => {
			if (typeof value === 'string') {
				return value !== '' || value !== null || value !== undefined;
			}

			return typeof value === 'boolean';
		};

		if (isNotEmpty()) {
			resetValidationErrors();
			const foundCondition = conditions.find(item => item.id === props.conditionId);
			setStatus('selected');
	
			if (foundCondition) {
				if (props.isMultiSelect) {
					setConditions(oldConditions => {
						const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
						const others = oldConditions.filter(item => item.id !== props.conditionId);

						const selectedValues = current.fieldMeetCertainCriteria!.selectValue.includes(value) ?
							current.fieldMeetCertainCriteria!.selectValue.filter(item => item !== value) :
							[...current.fieldMeetCertainCriteria!.selectValue, value];

						return [...others, {
							...current, fieldMeetCertainCriteria: {
								...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
								selectValue: selectedValues
							}
						}];
					});
				}

				if (!props.isMultiSelect) {
					setConditions(oldConditions => {
						const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
						const others = oldConditions.filter(item => item.id !== props.conditionId);
		
						return [...others, {
							...current, fieldMeetCertainCriteria: {
								...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
								selectValue: [value],
							}
						}];
					});
				}
			}
		}
	}, [props.isMultiSelect]);
  
	const valueRender = useMemo(() => {
		if (current && current.selectValue && status === 'selected') {
			return options.filter((item) => current.selectValue.includes(item.value)).map((item) => item.label).join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => current.selectValue.includes(item.value));

		if (current.selectValue && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [current, options]);

	const getOptionsAsync = useCallback(async () => {
		const resultOptions = await selectInputHook.loadOptions({
			organizationName: applicationContext.organizationName,
			projectId: applicationContext.projectId,
			fieldName: current.workItemField,
			userId: applicationContext.userId,
		});

		setOptions(resultOptions);
	}, [current.workItemField]);

	const clearOptionSelect = useCallback(() => {
		const foundCondition = conditions.find(item => item.id === props.conditionId);
		setStatus('selected');

		if (foundCondition) {
			setConditions(oldConditions => {
				const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
				const others = oldConditions.filter(item => item.id !== props.conditionId);

				return [...others, {
					...current, fieldMeetCertainCriteria: {
						...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
						selectValue: []
					}
				}];
			});
		}
	}, [conditions, props.conditionId]);

	useEffect(() => {
		setOnInitialLoading(true);
		getOptionsAsync()
			.finally(() => {
				setOnInitialLoading(false);
			});
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable
				inputMode='search'
				autoComplete='off'
				multiselect={props.isMultiSelect}
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				clearIcon={<ClearebleButtonInput onClick={clearOptionSelect} />}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					getOptionsAsync();
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
				{options.length === 0 && (
					<div className={classes.noOptions}>
						<span>No options</span>
					</div>
				)}
			</Combobox>
		</Field>
	);
}